import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, Button, Grid, InputAdornment, Stack, TextField, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import VideocamIcon from '@mui/icons-material/Videocam'
import HdIcon from '@mui/icons-material/Hd'
import BaseModal from '@ui/modal/BaseModal'

import {IDeviceVideoStream, IVideoStream} from '@containers/video/videoStreams/types'
import styles from './sx'
import {VideoStreamCard} from '@containers/video/videoStreams/list/VideoStreamCard'
import NotItems from '@ui/notItems/NotItems'
import {usePaginationCustom} from '@hooks/pagination/usePaginationCustom'
import PaginationCustom from '@ui/paginationCustom/PaginationCustom'
import {useQueryState} from '@hooks/query/useQueryState'
import {deleteStreamApi, getGetDeviceVideoStreamsListApi} from '@containers/video/videoStreams/api/api'
import SpinnerCustom from '@ui/spinner/SpinnerCustom'
import {useSnackBar} from '@hooks/alert/useSnackBar'
import {useShowError} from '@hooks/useShowError/useShowError'
import {DeviceCameraPlayer} from '@containers/video/videoStreams/cameraPlayer/DeviceCameraPlayer'
import SelectPerPage from '@ui/select/base/selectPerPage/SelectPerPage'

const DAHUA_CAMERA_TYPE = 'DahuaDevice'

interface IProps {
	videoStreams: IVideoStream[]
	accountId: number
}

const PER_PAGES: {
	value: string,
	label: string
}[] = [
	{
		label: '8',
		value: '8'
	},
	{
		label: '12',
		value: '12'
	},
	{
		label: '24',
		value: '24'
	},
	{
		label: 'Всі',
		value: '0'
	},
]
export const VideoStreamsList = ({videoStreams, accountId}: IProps) => {
	
	const [page, setPage] = useQueryState<number>('page', 1, {
		parse: (value) => Number(value)
	})
	const [searchValue, setSearchValue] = useQueryState<string>('search', '')
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [devices, setDevices] = useState<IDeviceVideoStream[]>([])
	const [pendingStream, setPendingStream] = useState<IVideoStream | null>(null)
	const [deviceId, setDeviceId] = useQueryState<number | null>('device', null, {
		parse: (value) => Number(value) || null
	})
	const [cameraSrc, setCameraSrc] = useQueryState<string | null>('camera', null)
	const [deviceName, setDeviceName] = useQueryState<string | null>('deviceName', null)
	const [perPage, setPerPage] = useQueryState<number | null>('perPage', 8, {
		parse: (value) => Number(value) || null
	})
	const {setSnackBar} = useSnackBar()
	const {onShowError} = useShowError()
	
	const targetVideoStreams: IVideoStream[] = useMemo(() => {
		if (!videoStreams?.length) {
			return []
		}
		if (!searchValue?.length) {
			return videoStreams
		}
		return videoStreams.filter((videoStream) => {
			const value = searchValue.trim().toLowerCase()
			return videoStream.licencePlate?.toLowerCase().includes(value) ||
				videoStream.deviceId?.toString().toLowerCase().includes(value) ||
				videoStream.hostName?.toLowerCase().includes(value) ||
				videoStream.cameras?.some((camera) => {
					return camera.cameraId?.toLowerCase().includes(value)
				})
		})
	}, [videoStreams, searchValue])
	
	const {
		totalPage,
		paginationData,
	} = usePaginationCustom({
		data: targetVideoStreams,
		page,
		perPage
	})
	
	const fetchAndOpen = useCallback(async (stream: IVideoStream, quality?: 'main') => {
		try {
			setIsLoading(true)
			const res = await getGetDeviceVideoStreamsListApi({
				accountId,
				deviceId: stream.deviceId,
				quality,
			})
			if (deviceId !== stream.deviceId) {
				setCameraSrc(null)
			}
			if (!res?.length) {
				setDeviceId(null)
				setDeviceName(null)
				setSnackBar({
					variant: 'warning',
					message: 'Нез знайдено жодних камер..'
				})
				return
			}
			setDeviceId(stream.deviceId)
			setDeviceName(stream.licencePlate)
			setDevices(res)
			if (
				res.length === 1 &&
				Object.keys(res[0].cameras).length === 1
			) {
				const camera = Object.values(res[0].cameras)[0]
				const value = camera.hd ?? camera.sd
				if (value) {
					setCameraSrc(value)
				}
			}
			setIsLoading(false)
		} catch (e) {
			setDevices([])
			setDeviceId(null)
			setDeviceName(null)
			setIsLoading(false)
			onShowError(e)
		}
	}, [accountId, deviceId])

	const onClickPlay = useCallback(async (stream: IVideoStream) => {
		// Для Dahua спершу запитуємо якість — стрім не провізіонуємо, поки користувач не обере SD/HD.
		if (stream.cameraType === DAHUA_CAMERA_TYPE) {
			setPendingStream(stream)
			return
		}
		await fetchAndOpen(stream)
	}, [fetchAndOpen])

	const onChooseQuality = useCallback((quality?: 'main') => {
		if (!pendingStream) {
			return
		}
		const stream = pendingStream
		setPendingStream(null)
		fetchAndOpen(stream, quality)
	}, [pendingStream, fetchAndOpen])

	useEffect(() => {
		const init = async () => {
			try {
				if (!deviceId) {
					return
				}
				const res = await getGetDeviceVideoStreamsListApi({
					accountId,
					deviceId,
				})
				setDevices(res)
			} catch (e) {
				console.log(e)
			}
		}
		init()
	}, [])
	
	return (
		<Box sx={styles.container}>
			
			{isLoading && <SpinnerCustom
                variant={'absolute'}
            />}
			
			<DeviceCameraPlayer
				devices={devices}
				open={devices.length >= 1}
				onClose={async () => {
					const currentStream = videoStreams.find((s) => s.deviceId === deviceId)
					if (currentStream?.cameras?.length) {
						await Promise.allSettled(
							currentStream.cameras.map((camera) =>
								deleteStreamApi({accountId, cameraId: camera.cameraId})
							)
						)
					}
					setDevices([])
					setCameraSrc(null)
				}}
				cameraSrc={cameraSrc}
				onChangeChangeSrc={setCameraSrc}
				deviceName={deviceName}
			/>

			<BaseModal
				open={!!pendingStream}
				onClose={() => setPendingStream(null)}
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					bgcolor: 'background.paper',
					borderRadius: 2,
					boxShadow: 24,
					width: 320,
					maxWidth: '90vw',
					p: 3,
				}}
			>
				<Stack spacing={2}>
					<Typography variant="h6">
						Оберіть якість
					</Typography>
					<Stack direction={'row'} spacing={2}>
						<Button
							fullWidth
							variant="outlined"
							startIcon={<VideocamIcon/>}
							onClick={() => onChooseQuality(undefined)}
						>
							SD
						</Button>
						<Button
							fullWidth
							variant="contained"
							startIcon={<HdIcon/>}
							onClick={() => onChooseQuality('main')}
						>
							HD
						</Button>
					</Stack>
				</Stack>
			</BaseModal>


			<Box sx={styles.header}>
				<TextField
					value={searchValue}
					onChange={(e) => setSearchValue(e.target.value)}
					placeholder="Пошук за номером або ID..."
					size="small"
					sx={styles.searchField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small"/>
							</InputAdornment>
						),
					}}
				/>
				
				<Stack
					direction={'row'}
					justifyContent={'space-between'}
					gap={2}
					alignItems={'center'}
				>
					<SelectPerPage
						perPage={perPage}
						setPage={setPage}
						setPerPage={setPerPage}
						data={PER_PAGES}
						width={'120px'}
					/>
					<Typography
						sx={{
							minWidth: '120px',
							
						}}
						variant="body2" color="text.secondary"
					>
						<span
							style={{fontWeight: 700}}>{paginationData?.length || 0} / {videoStreams?.length || 0} </span>пристроїв
					</Typography>
				</Stack>
			</Box>
			
			{!paginationData?.length && <NotItems/>}
			
			<Grid container spacing={2}>
				{paginationData?.length >= 1 && paginationData.map((stream) => (
					<Grid item xs={12} sm={6} md={4} lg={3} key={stream.deviceId}>
						<VideoStreamCard
							stream={stream}
							onClickPlay={onClickPlay}
							disabled={isLoading}
						/>
					</Grid>
				))}
			</Grid>
			
			
			{totalPage > 1 && <PaginationCustom
                onChange={setPage}
                page={page}
                totalPage={totalPage}
                sx={{
					marginTop: '20px'
				}}
            />}
		</Box>
	)
}
